import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { trackEvent } from '@/lib/analytics';
import {
  createCollection as apiCreateCollection,
  createSavedItem as apiCreateSavedItem,
  deleteCollection as apiDeleteCollection,
  deleteSavedItem as apiDeleteSavedItem,
  fetchSaved,
  renameCollection as apiRenameCollection,
  updateSavedItem as apiUpdateSavedItem,
  type SavedCollection,
  type SavedItem,
  type SavedStatus,
} from '@/lib/saved-api';

interface SavedSnapshot {
  items: SavedItem[];
  collections: SavedCollection[];
}

const SAVED_QUERY_KEY = ['saved-pieces'] as const;

const EMPTY: SavedSnapshot = { items: [], collections: [] };

export function useSaved() {
  const { customer } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const enabled = Boolean(customer);

  const query = useQuery<SavedSnapshot>({
    queryKey: SAVED_QUERY_KEY,
    queryFn: () => fetchSaved(),
    enabled,
    // Project convention (CLAUDE.md). Prevents background refetches from
    // racing with the optimistic-update machinery below.
    staleTime: Infinity,
  });

  const data = query.data ?? EMPTY;

  // O(1) lookup so SaveButton on every ProductCard doesn't do an O(n) scan
  // per render. Shop pages render dozens of cards; without this, isSaved
  // costs ~items × cards comparisons per render.
  const savedSet = useMemo(
    () => new Set(data.items.map((i) => i.productId)),
    [data.items],
  );

  const saveMutation = useMutation({
    mutationFn: (productId: string) => apiCreateSavedItem({ productId }),
    onMutate: async (productId) => {
      await queryClient.cancelQueries({ queryKey: SAVED_QUERY_KEY });
      const previous = queryClient.getQueryData<SavedSnapshot>(SAVED_QUERY_KEY);
      queryClient.setQueryData<SavedSnapshot>(SAVED_QUERY_KEY, (prev) => {
        const base = prev ?? EMPTY;
        if (base.items.some((i) => i.productId === productId)) return base;
        const placeholder: SavedItem = {
          id: `optimistic-${productId}`,
          productId,
          collectionIds: [],
          status: 'wishing',
          savedAt: new Date().toISOString(),
          purchasedAt: null,
          product: {
            id: productId,
            name: null,
            brand: null,
            image: null,
            url: null,
            price: null,
            originalPrice: null,
            currency: null,
            availability: null,
            isDelisted: false,
          },
        };
        return { ...base, items: [placeholder, ...base.items] };
      });
      return { previous };
    },
    onError: (_err, _productId, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(SAVED_QUERY_KEY, ctx.previous);
      }
      toast({
        title: "Couldn't save right now",
        description: 'Please try again in a moment.',
        variant: 'destructive',
      });
    },
    onSuccess: (item) => {
      queryClient.setQueryData<SavedSnapshot>(SAVED_QUERY_KEY, (prev) => {
        const base = prev ?? EMPTY;
        // If a fast unsave already removed the optimistic placeholder
        // before this POST resolved, respect that — re-inserting would
        // revive a save the user already cancelled.
        const exists = base.items.some((i) => i.productId === item.productId);
        if (!exists) return base;
        const others = base.items.filter((i) => i.productId !== item.productId);
        return { ...base, items: [item, ...others] };
      });
    },
  });

  const unsaveMutation = useMutation({
    mutationFn: (productId: string) => apiDeleteSavedItem(productId),
    onMutate: async (productId) => {
      await queryClient.cancelQueries({ queryKey: SAVED_QUERY_KEY });
      const previous = queryClient.getQueryData<SavedSnapshot>(SAVED_QUERY_KEY);
      queryClient.setQueryData<SavedSnapshot>(SAVED_QUERY_KEY, (prev) => {
        const base = prev ?? EMPTY;
        return {
          ...base,
          items: base.items.filter((i) => i.productId !== productId),
        };
      });
      return { previous };
    },
    onError: (_err, _productId, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(SAVED_QUERY_KEY, ctx.previous);
      }
      toast({
        title: "Couldn't update saved pieces",
        description: 'Please try again in a moment.',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (input: {
      productId: string;
      patch: { status?: SavedStatus; collectionIds?: string[] };
    }) => apiUpdateSavedItem(input.productId, input.patch),
    // Optimistic: apply the patch immediately so the AddToListsSheet
    // checkboxes feel instant. Snapshot the previous state for rollback.
    onMutate: async ({ productId, patch }) => {
      await queryClient.cancelQueries({ queryKey: SAVED_QUERY_KEY });
      const previous = queryClient.getQueryData<SavedSnapshot>(SAVED_QUERY_KEY);
      queryClient.setQueryData<SavedSnapshot>(SAVED_QUERY_KEY, (prev) => {
        const base = prev ?? EMPTY;
        return {
          ...base,
          items: base.items.map((i) =>
            i.productId === productId
              ? {
                  ...i,
                  ...(patch.status !== undefined ? { status: patch.status } : {}),
                  ...(patch.collectionIds !== undefined
                    ? { collectionIds: patch.collectionIds }
                    : {}),
                }
              : i,
          ),
          collections: recountCollections(
            base.collections,
            base.items,
            productId,
            patch.collectionIds,
          ),
        };
      });
      return { previous };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(SAVED_QUERY_KEY, ctx.previous);
      }
      toast({
        title: "Couldn't update saved pieces",
        description: 'Please try again in a moment.',
        variant: 'destructive',
      });
    },
    onSuccess: (item) => {
      queryClient.setQueryData<SavedSnapshot>(SAVED_QUERY_KEY, (prev) => {
        const base = prev ?? EMPTY;
        return {
          ...base,
          items: base.items.map((i) => (i.productId === item.productId ? item : i)),
        };
      });
    },
  });

  const createCollectionMutation = useMutation({
    mutationFn: (name: string) => apiCreateCollection(name),
    onSuccess: (collection) => {
      queryClient.setQueryData<SavedSnapshot>(SAVED_QUERY_KEY, (prev) => {
        const base = prev ?? EMPTY;
        return { ...base, collections: [collection, ...base.collections] };
      });
    },
  });

  const renameCollectionMutation = useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      apiRenameCollection(input.id, input.name),
    onSuccess: (collection) => {
      queryClient.setQueryData<SavedSnapshot>(SAVED_QUERY_KEY, (prev) => {
        const base = prev ?? EMPTY;
        return {
          ...base,
          collections: base.collections.map((c) =>
            c.id === collection.id ? collection : c,
          ),
        };
      });
    },
  });

  const deleteCollectionMutation = useMutation({
    mutationFn: (id: string) => apiDeleteCollection(id),
    onSuccess: (_void, id) => {
      queryClient.setQueryData<SavedSnapshot>(SAVED_QUERY_KEY, (prev) => {
        const base = prev ?? EMPTY;
        // The join table has ON DELETE CASCADE, so items lose their
        // membership for the dropped list and fall back to the virtual
        // default (zero memberships) wherever they had only this one.
        return {
          ...base,
          collections: base.collections.filter((c) => c.id !== id),
          items: base.items.map((i) =>
            i.collectionIds.includes(id)
              ? { ...i, collectionIds: i.collectionIds.filter((c) => c !== id) }
              : i,
          ),
        };
      });
    },
  });

  const isSaved = useCallback(
    (productId: string) => savedSet.has(productId),
    [savedSet],
  );

  const toggleSave = useCallback(
    (productId: string) => {
      if (!enabled) return;
      if (isSaved(productId)) {
        unsaveMutation.mutate(productId);
        trackEvent('unsave_piece', { product_id: productId });
      } else {
        saveMutation.mutate(productId);
        trackEvent('save_piece', { product_id: productId });
      }
    },
    [enabled, isSaved, saveMutation, unsaveMutation],
  );

  // Convenience wrappers that compute the new array from current state and
  // dispatch a single setCollections call. Keeps callers from having to do
  // their own array math for the common toggle case.
  const itemsByProductId = useMemo(() => {
    const map = new Map<string, SavedItem>();
    for (const i of data.items) map.set(i.productId, i);
    return map;
  }, [data.items]);

  const setCollections = useCallback(
    (productId: string, collectionIds: string[]) => {
      // De-dupe defensively — checkbox toggles can race in rare cases.
      const unique = Array.from(new Set(collectionIds));
      updateMutation.mutate({ productId, patch: { collectionIds: unique } });
    },
    [updateMutation],
  );

  const addToCollection = useCallback(
    (productId: string, collectionId: string) => {
      const current = itemsByProductId.get(productId)?.collectionIds ?? [];
      if (current.includes(collectionId)) return;
      setCollections(productId, [...current, collectionId]);
    },
    [itemsByProductId, setCollections],
  );

  const removeFromCollection = useCallback(
    (productId: string, collectionId: string) => {
      const current = itemsByProductId.get(productId)?.collectionIds ?? [];
      if (!current.includes(collectionId)) return;
      setCollections(
        productId,
        current.filter((id) => id !== collectionId),
      );
    },
    [itemsByProductId, setCollections],
  );

  return {
    enabled,
    items: data.items,
    collections: data.collections,
    isLoading: query.isLoading,
    isError: query.isError,
    isSaved,
    toggleSave,
    setStatus: (productId: string, status: SavedStatus) =>
      updateMutation.mutate({ productId, patch: { status } }),
    setCollections,
    addToCollection,
    removeFromCollection,
    createCollection: (name: string) => createCollectionMutation.mutateAsync(name),
    renameCollection: (id: string, name: string) =>
      renameCollectionMutation.mutateAsync({ id, name }),
    deleteCollection: (id: string) => deleteCollectionMutation.mutateAsync(id),
  };
}

// Recompute itemCount on each affected collection so the sidebar pills stay
// in sync with the optimistic membership change. Only the deltas matter:
// collections the item left lose 1, collections it joined gain 1.
function recountCollections(
  collections: SavedCollection[],
  items: SavedItem[],
  productId: string,
  nextIds: string[] | undefined,
): SavedCollection[] {
  if (nextIds === undefined) return collections;
  const prev = items.find((i) => i.productId === productId)?.collectionIds ?? [];
  const next = new Set(nextIds);
  const prevSet = new Set(prev);
  return collections.map((c) => {
    const wasIn = prevSet.has(c.id);
    const isIn = next.has(c.id);
    if (wasIn === isIn) return c;
    return { ...c, itemCount: c.itemCount + (isIn ? 1 : -1) };
  });
}
